import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { MonogramSmall } from "@/components/Monogram";

const STATUSES = ["pending", "confirmed", "completed", "cancelled"];

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("bookings");

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F2ED]">
        <span className="eyebrow text-[#4A3E39]">Checking session…</span>
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace />;

  const onLogout = () => {
    logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-[#F5F2ED]" data-testid="admin-dashboard">
      <header className="border-b border-[#D7CABC] bg-[#F5F2ED]/95 backdrop-blur-sm sticky top-0 z-30">
        <div className="px-6 lg:px-14 py-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <MonogramSmall size={26} />
            <div>
              <p className="font-display text-xl tracking-[0.18em] leading-none">T&amp;S IVORY</p>
              <p className="eyebrow text-[#B7AB9B] mt-1">Admin Portal</p>
            </div>
          </div>
          <div className="flex items-center gap-6">
            <a href="/" className="eyebrow text-[#4A3E39] hover:text-[#2C2724]" data-testid="admin-view-site">View site</a>
            <span className="hidden md:inline text-sm text-[#4A3E39]">{user.email}</span>
            <button onClick={onLogout} data-testid="admin-logout" className="eyebrow text-[#6D1F2B] border-b border-[#6D1F2B] pb-0.5">
              Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="px-6 lg:px-14 pt-12">
        <div className="flex items-center gap-8 border-b border-[#D7CABC]">
          {[
            { id: "bookings", label: "Bookings" },
            { id: "waitlist", label: "Waitlist" },
            { id: "articles", label: "The Ivory Edit" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`eyebrow pb-4 -mb-px border-b transition-colors ${
                tab === t.id ? "text-[#2C2724] border-[#2C2724]" : "text-[#B7AB9B] border-transparent hover:text-[#4A3E39]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <main className="px-6 lg:px-14 py-12">
        {tab === "bookings" && <BookingsTab />}
        {tab === "waitlist" && <WaitlistTab />}
        {tab === "articles" && <ArticlesTab />}
      </main>
    </div>
  );
}

function BookingsTab() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/bookings");
      setBookings(res.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const update = async (id, status) => {
    try {
      await api.patch(`/admin/bookings/${id}`, { status });
      setBookings((bs) => bs.map((b) => (b.id === id ? { ...b, status } : b)));
      toast.success(`Marked ${status}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const del = async (id) => {
    if (!confirm("Delete this booking?")) return;
    try {
      await api.delete(`/admin/bookings/${id}`);
      setBookings((bs) => bs.filter((b) => b.id !== id));
      toast.success("Booking deleted");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const filtered = filter === "all" ? bookings : bookings.filter((b) => b.status === filter);

  return (
    <section data-testid="admin-bookings">
      <div className="flex items-end justify-between mb-10">
        <div>
          <p className="eyebrow text-[#4A3E39] mb-3">Appointments</p>
          <h2 className="font-display text-4xl md:text-5xl text-[#2C2724]">Bookings</h2>
        </div>
        <div className="flex gap-2 flex-wrap">
          {["all", ...STATUSES].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              data-testid={`booking-filter-${s}`}
              className={`eyebrow px-3 py-2 border transition-colors ${
                filter === s ? "bg-[#2C2724] text-[#F5F2ED] border-[#2C2724]" : "border-[#B7AB9B] text-[#4A3E39] hover:border-[#2C2724]"
              }`}
            >
              {s} {filter === s ? `(${filtered.length})` : ""}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <p className="text-[#4A3E39]">Loading bookings…</p>
      ) : filtered.length === 0 ? (
        <p className="text-[#4A3E39] py-12 italic font-display text-xl">No bookings to show.</p>
      ) : (
        <div className="space-y-4">
          {filtered.map((b) => (
            <div key={b.id} data-testid={`booking-row-${b.id}`} className="border border-[#D7CABC] bg-[#FBF8F3] p-6 grid grid-cols-1 md:grid-cols-12 gap-6 items-start">
              <div className="md:col-span-3">
                <div className="font-display text-2xl text-[#2C2724]">{b.name}</div>
                <div className="text-sm text-[#4A3E39] mt-1">{b.phone}</div>
                {b.email && <div className="text-sm text-[#B7AB9B] mt-0.5">{b.email}</div>}
              </div>
              <div className="md:col-span-3">
                <p className="eyebrow text-[#B7AB9B]">Service</p>
                <p className="mt-1 text-[15px] text-[#2C2724]">{b.service}</p>
                <p className="text-sm text-[#4A3E39] mt-0.5">{b.preferred_date} · {b.preferred_time}</p>
              </div>
              <div className="md:col-span-3">
                {b.notes && (
                  <>
                    <p className="eyebrow text-[#B7AB9B]">Notes</p>
                    <p className="mt-1 text-sm text-[#4A3E39] italic font-display">{b.notes}</p>
                  </>
                )}
                <p className="text-xs text-[#B7AB9B] mt-2">
                  {new Date(b.created_at).toLocaleString()}
                </p>
              </div>
              <div className="md:col-span-3 flex flex-col gap-3">
                <select
                  value={b.status}
                  onChange={(e) => update(b.id, e.target.value)}
                  data-testid={`booking-status-${b.id}`}
                  className={`editorial-input border border-[#B7AB9B] px-3 py-2 ${statusColor(b.status)}`}
                >
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <button
                  onClick={() => del(b.id)}
                  data-testid={`booking-delete-${b.id}`}
                  className="eyebrow text-[#6D1F2B] self-end hover:underline"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function statusColor(s) {
  return {
    pending: "text-[#4A3E39]",
    confirmed: "text-[#6D1F2B] font-medium",
    completed: "text-[#AEB4A1]",
    cancelled: "text-[#B7AB9B] line-through",
  }[s] || "";
}

function WaitlistTab() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/waitlist");
      setEntries(res.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = (() => {
    const q = query.trim().toLowerCase();
    const sorted = [...entries].sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    );
    if (!q) return sorted;
    return sorted.filter(
      (e) =>
        (e.name || "").toLowerCase().includes(q) ||
        (e.phone || "").toLowerCase().includes(q)
    );
  })();

  const formatDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  const exportCsv = () => {
    if (!filtered.length) {
      toast.error("Nothing to export.");
      return;
    }
    const header = ["Name", "Phone", "Preferred Service", "Email", "Created At"];
    const escape = (v) => {
      const s = (v ?? "").toString().replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    };
    const rows = filtered.map((e) => [
      escape(e.name),
      escape(e.phone),
      escape(e.service_category),
      escape(e.email || ""),
      escape(e.created_at),
    ].join(","));
    const csv = [header.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `tsivory-waitlist-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} record${filtered.length === 1 ? "" : "s"}`);
  };

  return (
    <section data-testid="admin-waitlist">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
        <div>
          <h2 className="font-display text-3xl md:text-4xl text-[#2C2724] leading-none">Waitlist</h2>
          <p className="eyebrow text-[#B7AB9B] mt-3">
            {loading ? "Loading…" : `${filtered.length} of ${entries.length} record${entries.length === 1 ? "" : "s"}`}
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <input
            data-testid="waitlist-search"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name or phone…"
            className="editorial-input min-w-[240px] md:min-w-[280px]"
            style={{ paddingTop: "0.4rem", paddingBottom: "0.4rem" }}
          />
          <button
            type="button"
            onClick={load}
            data-testid="waitlist-refresh"
            disabled={loading}
            className="eyebrow border border-[#B7AB9B] text-[#4A3E39] px-4 py-2.5 hover:border-[#2C2724] hover:text-[#2C2724] transition-colors disabled:opacity-50"
          >
            {loading ? "Refreshing…" : "Refresh"}
          </button>
          <button
            type="button"
            onClick={exportCsv}
            data-testid="waitlist-export"
            className="eyebrow bg-[#2C2724] text-[#F5F2ED] px-4 py-2.5 hover:bg-[#4A3E39] transition-colors"
          >
            Export CSV
          </button>
        </div>
      </div>

      {loading ? (
        <p className="eyebrow text-[#B7AB9B]">Loading waitlist…</p>
      ) : filtered.length === 0 ? (
        <p className="eyebrow text-[#B7AB9B]" data-testid="waitlist-empty">
          {entries.length === 0 ? "No waitlist entries yet." : "No results match your search."}
        </p>
      ) : (
        <div className="overflow-x-auto border border-[#D7CABC]">
          <table className="w-full text-left" data-testid="waitlist-table">
            <thead className="bg-[#EEE7DE]">
              <tr>
                <th className="eyebrow text-[#4A3E39] px-5 py-4">Name</th>
                <th className="eyebrow text-[#4A3E39] px-5 py-4">Phone</th>
                <th className="eyebrow text-[#4A3E39] px-5 py-4">Preferred Service</th>
                <th className="eyebrow text-[#4A3E39] px-5 py-4">Created At</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((e) => (
                <tr
                  key={e.id}
                  data-testid={`waitlist-row-${e.id}`}
                  className="border-t border-[#D7CABC] hover:bg-[#F5F2ED]/60 transition-colors"
                >
                  <td className="px-5 py-4 font-display text-lg text-[#2C2724]">{e.name}</td>
                  <td className="px-5 py-4 text-[#4A3E39]">
                    <a href={`tel:${e.phone}`} className="hover:underline">{e.phone}</a>
                  </td>
                  <td className="px-5 py-4">
                    <span className="eyebrow text-[#4A3E39]">{e.service_category}</span>
                  </td>
                  <td className="px-5 py-4 text-[#4A3E39] text-sm whitespace-nowrap">
                    {formatDate(e.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}


function ArticlesTab() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const blank = {
    title: "",
    subtitle: "",
    category: "Editorial",
    excerpt: "",
    hero_image: "",
    secondary_image: "",
    pull_quote: "",
    reading_time: 4,
    body: "",
    published: true,
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/articles");
      setArticles(res.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (data) => {
    try {
      if (editing?.id) {
        await api.patch(`/admin/articles/${editing.id}`, data);
        toast.success("Article updated");
      } else {
        await api.post("/admin/articles", data);
        toast.success("Article created");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const del = async (id) => {
    if (!confirm("Delete this article?")) return;
    try {
      await api.delete(`/admin/articles/${id}`);
      setArticles((a) => a.filter((x) => x.id !== id));
      toast.success("Article deleted");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (editing) {
    return (
      <ArticleEditor
        initial={editing.id ? editing : blank}
        onCancel={() => setEditing(null)}
        onSave={save}
      />
    );
  }

  return (
    <section data-testid="admin-articles">
      <div className="flex items-end justify-between mb-10">
        <div>
          <p className="eyebrow text-[#4A3E39] mb-3">Editorial</p>
          <h2 className="font-display text-4xl md:text-5xl text-[#2C2724]">The Ivory Edit</h2>
        </div>
        <button onClick={() => setEditing(blank)} data-testid="admin-new-article" className="btn-burgundy">
          New Article
        </button>
      </div>

      {loading ? (
        <p className="text-[#4A3E39]">Loading articles…</p>
      ) : articles.length === 0 ? (
        <p className="text-[#4A3E39] py-12 italic font-display text-xl">No articles yet — write your first.</p>
      ) : (
        <div className="space-y-6">
          {articles.map((a) => (
            <div key={a.id} data-testid={`article-row-${a.id}`} className="border border-[#D7CABC] bg-[#FBF8F3] p-6 grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
              <div className="md:col-span-2">
                <div className="aspect-square overflow-hidden bg-[#EEE7DE]">
                  {a.hero_image && (
                    <img src={a.hero_image} alt="" className="w-full h-full object-cover" />
                  )}
                </div>
              </div>
              <div className="md:col-span-7">
                <p className="eyebrow text-[#B7AB9B]">{a.category} · {a.reading_time} min · /{a.slug}</p>
                <h3 className="font-display text-2xl text-[#2C2724] mt-2">{a.title}</h3>
                {a.subtitle && <p className="text-sm italic text-[#4A3E39] mt-1">{a.subtitle}</p>}
                <p className="text-sm text-[#4A3E39] mt-2 line-clamp-2">{a.excerpt}</p>
              </div>
              <div className="md:col-span-3 flex flex-col items-stretch gap-3">
                <span className={`eyebrow text-center py-2 border ${a.published ? "border-[#AEB4A1] text-[#4A3E39]" : "border-[#B7AB9B] text-[#B7AB9B]"}`}>
                  {a.published ? "Published" : "Draft"}
                </span>
                <button onClick={() => setEditing(a)} data-testid={`article-edit-${a.id}`} className="eyebrow border border-[#2C2724] py-2 hover:bg-[#2C2724] hover:text-[#F5F2ED] transition-colors">
                  Edit
                </button>
                <button onClick={() => del(a.id)} data-testid={`article-delete-${a.id}`} className="eyebrow text-[#6D1F2B] hover:underline">
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function ArticleEditor({ initial, onCancel, onSave }) {
  const [form, setForm] = useState(initial);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  return (
    <section data-testid="admin-article-editor">
      <div className="flex items-end justify-between mb-10">
        <div>
          <p className="eyebrow text-[#4A3E39] mb-3">{initial.id ? "Editing" : "New article"}</p>
          <h2 className="font-display text-4xl md:text-5xl text-[#2C2724]">{form.title || "Untitled"}</h2>
        </div>
        <div className="flex gap-4">
          <button onClick={onCancel} data-testid="article-editor-cancel" className="btn-ghost">Cancel</button>
          <button onClick={() => onSave(form)} data-testid="article-editor-save" className="btn-burgundy">Save</button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-8 space-y-8">
          <Field label="Title" testid="article-title">
            <input className="editorial-input" value={form.title} onChange={set("title")} />
          </Field>
          <Field label="Subtitle" testid="article-subtitle">
            <input className="editorial-input" value={form.subtitle} onChange={set("subtitle")} />
          </Field>
          <Field label="Excerpt" testid="article-excerpt">
            <textarea className="editorial-input resize-none" rows={3} value={form.excerpt} onChange={set("excerpt")} />
          </Field>
          <Field label="Body (paragraphs separated by blank lines)" testid="article-body">
            <textarea className="editorial-input resize-none font-mono text-[13px]" rows={14} value={form.body} onChange={set("body")} />
          </Field>
          <Field label="Pull Quote" testid="article-pull-quote">
            <textarea className="editorial-input resize-none italic font-display text-lg" rows={2} value={form.pull_quote} onChange={set("pull_quote")} />
          </Field>
        </div>
        <div className="lg:col-span-4 space-y-8">
          <Field label="Category" testid="article-category">
            <input className="editorial-input" value={form.category} onChange={set("category")} />
          </Field>
          <Field label="Reading time (min)" testid="article-reading-time">
            <input className="editorial-input" type="number" value={form.reading_time} onChange={set("reading_time")} />
          </Field>
          <Field label="Hero image URL" testid="article-hero-image">
            <input className="editorial-input" value={form.hero_image} onChange={set("hero_image")} />
            {form.hero_image && <img src={form.hero_image} alt="" className="mt-3 w-full aspect-[4/3] object-cover" />}
          </Field>
          <Field label="Secondary image URL" testid="article-secondary-image">
            <input className="editorial-input" value={form.secondary_image || ""} onChange={set("secondary_image")} />
            {form.secondary_image && <img src={form.secondary_image} alt="" className="mt-3 w-full aspect-[4/3] object-cover" />}
          </Field>
          <label className="flex items-center gap-3 cursor-pointer" data-testid="article-published">
            <input type="checkbox" checked={form.published} onChange={set("published")} className="w-4 h-4 accent-[#6D1F2B]" />
            <span className="eyebrow">Published</span>
          </label>
        </div>
      </div>
    </section>
  );
}

function Field({ label, testid, children }) {
  return (
    <div data-testid={testid}>
      <label className="eyebrow text-[#4A3E39] mb-3 block">{label}</label>
      {children}
    </div>
  );
}
